import { useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { api, messageOf, type ConversationDetailResponse, type ConversationResponse } from "../lib/api.ts";
import { useToken } from "../lib/auth.tsx";
import type { AgentOutlet } from "./AgentLayout.tsx";

export function Conversations() {
  const { agent } = useOutletContext<AgentOutlet>();
  const token = useToken();
  const [conversations, setConversations] = useState<ConversationResponse[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    void api<{ conversations: ConversationResponse[] }>(`/v1/agents/${agent.id}/conversations`, token)
      .then((body) => {
        if (active) setConversations(body.conversations);
      })
      .catch((caught: unknown) => {
        if (active) setError(messageOf(caught));
      });
    return () => {
      active = false;
    };
  }, [agent.id, token]);

  return (
    <div className="stack">
      <p className="lead">Chats from your site and from the practice bubble show up here, with the sources that were shown.</p>
      {error ? <p className="banner bad">{error}</p> : null}
      {conversations === null ? <p className="muted">Loading conversations…</p> : null}
      {conversations?.length === 0 ? <p className="muted">No chats yet. Ask a question in the chat bubble and it will show up here.</p> : null}
      <ul className="doc-list">
        {conversations?.map((conversation) => (
          <li key={conversation.id}>
            <Link className="card doc" to={`/app/agents/${agent.id}/conversations/${conversation.id}`}>
              <div>
                <strong>{conversation.preview || "New conversation"}</strong>
                <p className="muted">
                  {label(conversation.channel)} · {new Date(conversation.createdAt).toLocaleString()}
                  {conversation.pageUrl ? ` · ${conversation.pageUrl}` : ""}
                </p>
              </div>
              <span className="status ready">{label(conversation.channel)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ConversationDetail() {
  const { agent } = useOutletContext<AgentOutlet>();
  const { conversationId } = useParams();
  const token = useToken();
  const [detail, setDetail] = useState<ConversationDetailResponse | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!conversationId) return;
    let active = true;
    void api<ConversationDetailResponse>(`/v1/agents/${agent.id}/conversations/${conversationId}`, token)
      .then((body) => {
        if (active) setDetail(body);
      })
      .catch((caught: unknown) => {
        if (active) setError(messageOf(caught));
      });
    return () => {
      active = false;
    };
  }, [agent.id, conversationId, token]);

  return (
    <div className="stack">
      <Link className="text-link" to={`/app/agents/${agent.id}/conversations`}>
        All chats
      </Link>
      {error ? <p className="banner bad">{error}</p> : null}
      {detail === null && !error ? <p className="muted">Loading transcript…</p> : null}
      {detail ? (
        <section className="card preview">
          <header>
            <p className="eyebrow">{label(detail.conversation.channel)}</p>
            <p className="muted">{new Date(detail.conversation.createdAt).toLocaleString()}</p>
          </header>
          <div className="log">
            {detail.messages.map((message) => (
              <article key={message.id} className={message.role}>
                <p>{message.content}</p>
                {message.citations.length > 0 ? (
                  <ul>
                    {message.citations.map((citation) => (
                      <li key={`${message.id}-${citation.index}`}>
                        <strong>{citation.label}</strong> {citation.excerpt}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function label(channel: ConversationResponse["channel"]): string {
  if (channel === "web") return "Your website";
  if (channel === "preview") return "Practice chat";
  return "Phone";
}
